import React from 'react';
import './Buttons.css';

interface BaseButtonProps {
	children: React.ReactNode;
	onClick: () => void;
	disabled?: boolean;
	className?: string;
	ariaLabel?: string;
}

export const PrimaryButton: React.FC<BaseButtonProps> = ({
	children,
	onClick,
	disabled = false,
	className = '',
	ariaLabel,
}) => {
	return (
		<button
			className={`primary-button ${className}`}
			onClick={onClick}
			disabled={disabled}
			aria-label={ariaLabel}
		>
			{children}
		</button>
	);
};

export const SecondaryButton: React.FC<BaseButtonProps> = ({
	children,
	onClick,
	disabled = false,
	className = '',
	ariaLabel,
}) => {
	return (
		<button
			className={`secondary-button ${className}`}
			onClick={onClick}
			disabled={disabled}
			aria-label={ariaLabel}
		>
			{children}
		</button>
	);
};

interface RefreshButtonProps {
	onRefresh: () => void;
	isRefreshing: boolean;
	disabled?: boolean;
	className?: string;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
	onRefresh,
	isRefreshing,
	disabled = false,
	className = '',
}) => {
	return (
		<a
			href="#"
			onClick={(e) => {
				e.preventDefault();
				if (!isRefreshing && !disabled) {
					onRefresh();
				}
			}}
			className={`refresh-button ${isRefreshing ? 'refreshing' : ''} ${disabled ? 'disabled' : ''} ${className}`}
			aria-label="Refresh devices"
		>
			{isRefreshing ? (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					className="refresh-button-spinner"
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						stroke="#0045db"
						strokeWidth="2"
						strokeLinecap="round"
						strokeDasharray="32"
						strokeDashoffset="8"
						fill="none"
					/>
				</svg>
			) : (
				<svg
					width="106"
					height="32"
					viewBox="0 0 106 32"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M21 18H16V22.9991"
						stroke="#0045DB"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M31.2223 18.9771C30.1335 22.1586 27.0427 24.212 23.6873 23.983C20.3319 23.754 17.5488 21.2999 16.9023 18"
						stroke="#0045DB"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M27 14H32V9.00098"
						stroke="#0045DB"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M16.7773 13.0232C17.8662 9.84173 20.957 7.78841 24.3124 8.01738C27.6678 8.24634 30.4509 10.7005 31.0973 14.0004"
						stroke="#0045DB"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M49.9625 20.5H48.6885L46.9725 16.4505H44.444V20.5H43.2415V11.101H46.8815C48.916 11.101 49.9365 12.0695 49.9365 13.6685C49.9365 15.196 49.0135 15.898 48.1165 16.236L49.9625 20.5ZM46.4915 15.456C47.902 15.456 48.708 14.8775 48.708 13.74C48.708 12.635 47.98 12.0825 46.7775 12.0825H44.444V15.456H46.4915ZM52.4605 17.419C52.4605 18.693 53.091 19.7005 54.3975 19.7005C55.2555 19.7005 55.9315 19.2975 56.1005 18.537H57.2315C56.9845 19.9085 55.7235 20.63 54.417 20.63C52.5385 20.63 51.245 19.3495 51.245 17.2045C51.245 15.0855 52.376 13.649 54.3325 13.649C56.224 13.649 57.277 14.936 57.277 16.977V17.419H52.4605ZM52.467 16.6065H56.12C56.12 15.4495 55.5415 14.559 54.326 14.559C53.0585 14.559 52.4605 15.6055 52.467 16.6065ZM62.4169 13.779V14.6565H60.7854V20.5H59.5829V14.6565H58.1594V13.779H59.5829V12.739C59.5829 11.673 60.1224 11.101 61.2079 11.101H62.4754L62.4819 11.9525H61.4809C60.9284 11.9525 60.7659 12.245 60.7659 12.843V13.779H62.4169ZM63.8275 20.5V13.779H64.9715V15.0725C65.3095 14.104 66.109 13.649 66.9605 13.649C67.1555 13.649 67.3635 13.675 67.474 13.7335V14.897C67.344 14.8385 67.1035 14.819 66.967 14.819C65.8945 14.7475 65.0235 15.2025 65.0235 16.3205V20.5H63.8275ZM69.6881 17.419C69.6881 18.693 70.3186 19.7005 71.6251 19.7005C72.4831 19.7005 73.1591 19.2975 73.3281 18.537H74.4591C74.2121 19.9085 72.9511 20.63 71.6446 20.63C69.7661 20.63 68.4726 19.3495 68.4726 17.2045C68.4726 15.0855 69.6036 13.649 71.5601 13.649C73.4516 13.649 74.5046 14.936 74.5046 16.977V17.419H69.6881ZM69.6946 16.6065H73.3476C73.3476 15.4495 72.7691 14.559 71.5536 14.559C70.2861 14.559 69.6881 15.6055 69.6946 16.6065ZM78.5346 20.63C77.1306 20.63 75.9346 19.993 75.7396 18.5435H76.8316C77.0006 19.33 77.6766 19.733 78.5281 19.733C79.4381 19.733 80.0426 19.4015 80.0426 18.6995C80.0426 18.2055 79.7566 17.9195 79.1456 17.7765L77.7546 17.432C76.6691 17.172 75.9736 16.6585 75.9671 15.6575C75.9606 14.481 76.8251 13.649 78.5281 13.649C79.9841 13.649 80.9526 14.3185 80.9981 15.5795H79.9321C79.8411 14.936 79.3471 14.533 78.5151 14.533C77.6571 14.533 77.0656 14.871 77.0656 15.586C77.0656 16.067 77.4556 16.3075 78.1966 16.4895L79.5486 16.8275C80.7836 17.1525 81.1216 17.9715 81.1216 18.6345C81.1216 19.8695 80.1726 20.63 78.5346 20.63ZM82.7054 20.5V10.841H83.8754V14.767C84.2134 14.2145 84.8959 13.6945 85.9749 13.6945C87.2879 13.6945 88.3214 14.468 88.3214 15.6965V20.5H87.1189V15.937C87.1189 15.1375 86.5144 14.676 85.6304 14.676C84.7204 14.676 83.9014 15.1115 83.9014 16.015V20.5H82.7054Z"
						fill="#0045DB"
					/>
				</svg>
			)}
		</a>
	);
};

interface MenuItemButtonProps {
	children: React.ReactNode;
	onClick: () => void;
	className?: string;
	ariaLabel?: string;
	role?: string;
}

export const MenuItemButton: React.FC<MenuItemButtonProps> = ({
	children,
	onClick,
	className = '',
	ariaLabel,
	role,
}) => {
	return (
		<button
			className={`menu-item-button ${className}`}
			type="button"
			onClick={onClick}
			aria-label={ariaLabel}
			role={role}
		>
			{children}
		</button>
	);
};

// Export all buttons as a namespace for convenience
export const Buttons = {
	Primary: PrimaryButton,
	Secondary: SecondaryButton,
	Refresh: RefreshButton,
	MenuItem: MenuItemButton,
};
